import { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { catalogAddEditValidators } from '../lib/validators';
import { currentItemSelector, selectItemsStatus, getItem, updateItem } from '../../../entities/product/model';
import { CATEGORY_SELECT_ITEMS } from '@shared/config/categories';
import { Button } from '@shared/ui/button';
import { LoadingWrapper } from '@shared/ui/loading-wrapper';
import { InputField } from '@shared/ui/input-field';
import minusCircle from '../../../shared/assets/sprites/minus_circle.svg';
import plusCircle from '../../../shared/assets/sprites/plus_circle.svg';
import redPlusCircle from '../../../shared/assets/sprites/big_red_plus_circle.svg';
import { TextareaField } from '@shared/ui/textarea-field';
import { useAppDispatch, useAppSelector } from '../../../app/store/store';
import ReactSelect from 'react-select';

const getCategoryLabel = (categoryValue: string) => {
  const category = CATEGORY_SELECT_ITEMS.find(
    (cat) => cat.value === categoryValue
  );
  return {
    label: category?.label || '',
    value: category?.value || '',
  }
};

const inputCls = 'w-full p-2 border border-[#dcdee0] rounded-[10px] text-[#393939] font-medium text-base';
const inputErrorCls = 'border-[#e3282a]';
const labelCls = 'mb-2 text-[#2d2c2c] text-base font-medium leading-5 block';
const errorCls = 'text-[#e3282a] text-sm';
const fieldCls = 'mt-4';
const fieldHeaderCls = 'flex justify-between';

const CatalogAddEdit = () => {
  const { id } = useParams();
  const product = useAppSelector(currentItemSelector);
  const dispatch = useAppDispatch();
  const status = useSelector(selectItemsStatus);
  const [photo, setPhoto] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(catalogAddEditValidators),
  });

  useEffect(() => {
    if (id) {
      dispatch(getItem({ id: Number(id) }))
    }
  }, [dispatch]);

  useEffect(() => {
    if (id) {
      const techSpecs = product?.tech_specs ? ((product.tech_specs as Record<string, unknown>).th as { label: string; value: string }[] | undefined) ?? [] : [];
      reset({ ...product, tech_specs: techSpecs, category: getCategoryLabel(product?.category) });
    }
  }, [dispatch, product]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'tech_specs',
  });

  const { fields: additionalMaterialsFields, append: appendAdditionalMaterial, remove: removeAdditionalMaterial } = useFieldArray({
    control,
    name: 'additional_materials',
  });

  interface CatalogFormData {
    tech_specs?: { label: string; value: string }[];
    category: { value?: string; label?: string };
    short_description: string;
    description: string;
    name: string;
    additional_materials?: { name: string; material_url: string }[];
    photo?: string;
  }

  const onSubmit = async (data: CatalogFormData) => {
    const { tech_specs, category, short_description, description, name, additional_materials } = data;
    if (id) {
      let formData = new FormData();
      formData.append('photo', photo ? photo : '');
      formData.append('tech_specs', JSON.stringify({ th: tech_specs }));
      formData.append('category', category.value ?? '');
      formData.append('short_description', short_description);
      formData.append('description', description);
      formData.append('name', name);
      (additional_materials ?? []).forEach((material) => {
        formData.append(`additional_materials`, JSON.stringify(material))
      });
      dispatch(
        updateItem({
          itemId: +id,
          body: formData
        })
      );
    }
  };

  const handlePhotoChange = (event: any) => {
    const newFile = event.target.files[0];
    if (newFile) {
      setPhoto(newFile)
      const newPreviewUrl = URL.createObjectURL(newFile)
      setValue('photo', newPreviewUrl)
    } else {
      setPhoto(null);
      setValue('photo', '')
    }
  };

  return (
    <LoadingWrapper type="main" status={status}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex justify-between gap-4 bg-white rounded-2xl py-12 px-8 max-md:flex-col"
      >
        {/* Left column */}
        <div className="w-full flex flex-col">
          <InputField
            id="name"
            inputName="name"
            label="Название товара"
            errors={errors}
            register={register}
          />
          <TextareaField
            id="short_description"
            name="short_description"
            label="Краткое описание"
            errors={errors}
            register={register}
            maxLength={512}
            currentVal={watch('short_description')}
          />
          <TextareaField
            id="description"
            name="description"
            label="Описание"
            errors={errors}
            register={register}
            maxLength={2000}
            currentVal={watch('description')}
          />
          <div className={fieldCls}>
            <label htmlFor={id} className={labelCls}>
              Технические характеристики
            </label>
            {fields?.map((spec, index) => (
              <div key={index} className="flex mb-5 items-center gap-[10px]">
                <div className={fieldCls + ' w-full'}>
                  <div className={fieldHeaderCls}>
                    <label htmlFor={`tech_specs.${index}.label`} className={labelCls}>
                      Название
                    </label>
                    {errors?.tech_specs?.[index]?.label && (
                      <p className={errorCls}>
                        {errors?.tech_specs?.[index]?.label?.message}
                      </p>
                    )}
                  </div>
                  <input
                    defaultValue={spec.label}
                    {...register(`tech_specs.${index}.label`)}
                    className={`${inputCls} ${errors?.tech_specs?.[index]?.label ? inputErrorCls : ''}`}
                  />
                </div>
                <div className={fieldCls + ' w-full'}>
                  <div className={fieldHeaderCls}>
                    <label htmlFor={`techSpecs[${index}].value`} className={labelCls}>
                      Значение
                    </label>
                    {errors?.tech_specs?.[index]?.value && (
                      <p className={errorCls}>
                        {errors?.tech_specs?.[index]?.value?.message}
                      </p>
                    )}
                  </div>
                  <input
                    defaultValue={spec.value}
                    {...register(`tech_specs.${index}.value`)}
                    className={`${inputCls} ${errors?.tech_specs?.[index]?.value ? inputErrorCls : ''}`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="bg-transparent border-0 cursor-pointer text-red-500 shrink-0"
                >
                  &#10005;
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => append({ label: '', value: '' })}
              className="border border-[#dcdee0] bg-white rounded-[10px] cursor-pointer h-12 mb-6 flex justify-center items-center mt-4 transition-colors hover:bg-[#dcdee0] w-full gap-2 px-4"
            >
              <img src={plusCircle} alt="Добавить характеристику" />
              <span>Добавить характеристику</span>
            </button>
          </div>
        </div>

        {/* Right column */}
        <div className="w-full flex flex-col">
          <div className={fieldCls}>
            <div className={fieldHeaderCls}>
              <label htmlFor="photo" className={labelCls}>
                Добавить фото *
              </label>
              {errors.photo && (
                <p className={errorCls}>{errors.photo.message}</p>
              )}
            </div>
            <div className="flex justify-start w-full items-center">
              <div className="relative w-1/2 h-[184px] group">
                {watch('photo') && (
                  <img alt="" src={watch('photo')} className="w-[267px] h-[184px] rounded" />
                )}
                <div
                  className={[
                    'cursor-pointer flex justify-center items-center w-[267px] h-[184px] rounded border-2 border-dashed border-[#e3282a]',
                    !watch('photo') ? '' : 'absolute top-0 invisible group-hover:visible',
                  ].join(' ')}
                >
                  <label htmlFor="photo" className="flex flex-col-reverse justify-center items-center w-1/2 cursor-pointer">
                    Выбрать фото
                    <img src={redPlusCircle} alt="" />
                  </label>
                </div>
                <input
                  id="photo"
                  type="file"
                  name="photo"
                  className="hidden"
                  onChange={handlePhotoChange}
                />
              </div>
            </div>
          </div>

          <div className={fieldCls}>
            <div className={fieldCls}>
              <label htmlFor="category" className={labelCls}>
                Категория *
              </label>
              {errors.category && (
                <p className={errorCls}>{errors.category.message}</p>
              )}
              <ReactSelect
                name="category"
                options={CATEGORY_SELECT_ITEMS}
                placeholder="Выберите категорию"
                value={watch('category')}
                onChange={(option: any) => {
                  setValue('category', option);
                }}
              />
            </div>
          </div>

          {additionalMaterialsFields?.map((material, index) => (
            <div key={index}>
              <div className={fieldCls}>
                <div className={fieldHeaderCls}>
                  <label htmlFor={`additional_materials[${index}].name`} className={labelCls}>
                    Доп. материал (название) *
                  </label>
                  {errors?.additional_materials?.[index]?.name && (
                    <p className={errorCls}>
                      {errors?.additional_materials?.[index]?.name?.message}
                    </p>
                  )}
                </div>
                <input
                  id={`additional_materials[${index}].name`}
                  defaultValue={material.name}
                  {...register(`additional_materials.${index}.name`, {
                    required: 'Обязательное поле',
                  })}
                  className={`${inputCls} ${errors?.additional_materials?.[index]?.name ? inputErrorCls : ''}`}
                />
              </div>
              <div className={fieldCls}>
                <div className={fieldHeaderCls}>
                  <label htmlFor={`additional_materials[${index}].material_url`} className={labelCls}>
                    Доп. материал (ссылка) *
                  </label>
                  {errors?.additional_materials?.[index]?.material_url && (
                    <p className={errorCls}>
                      {errors?.additional_materials?.[index]?.material_url?.message}
                    </p>
                  )}
                </div>
                <input
                  id={`additional_materials[${index}].material_url`}
                  type="url"
                  defaultValue={material.material_url}
                  {...register(`additional_materials.${index}.material_url`, {
                    required: 'Обязательное поле',
                  })}
                  className={`${inputCls} ${errors?.additional_materials?.[index]?.material_url ? inputErrorCls : ''}`}
                />
              </div>
              <button
                type="button"
                onClick={() => removeAdditionalMaterial(index)}
                className="border border-[#dcdee0] bg-white rounded-[10px] cursor-pointer h-12 mb-6 flex justify-center items-center mt-4 transition-colors hover:bg-[#dcdee0] w-full gap-2 px-4"
              >
                <img src={minusCircle} alt="Удалить материал" />
                <span>Удалить материал</span>
              </button>
            </div>
          ))}

          {additionalMaterialsFields?.length < 4 && (
            <button
              type="button"
              onClick={() => {
                appendAdditionalMaterial({ name: '', material_url: '' });
              }}
              className="border border-[#dcdee0] bg-white rounded-[10px] cursor-pointer h-12 mb-6 flex justify-center items-center mt-4 transition-colors hover:bg-[#dcdee0] w-full gap-2 px-4"
            >
              <img src={plusCircle} alt="Добавить материал" />
              <span>Добавить доп. материал</span>
            </button>
          )}
          <Button type="submit" className="mt-4">
            Сохранить
          </Button>
        </div>
      </form>
    </LoadingWrapper>
  );
};

export default CatalogAddEdit;
